import type { Question } from './questions';
import { MATH_TOPIC_LABELS, MathTopic } from './settings';
import { questionChoices } from './questionChoices';

const bank: Question[] = [];
const format = (value: number, digits: number) => Number(value.toFixed(digits)).toString();
const signedPercent = (rate: number) => rate < 0 ? '(−' + format(Math.abs(rate), 1) + '%)' : format(rate, 1) + '%';
const factor = (rate: number) => '(1 ' + (rate < 0 ? '− ' : '+ ') + format(Math.abs(rate), 1) + '%)';
const numeric = (topic: Exclude<MathTopic, 'all'>, index: number, stem: string, value: number,
  digits: number, step: number, suffix = '', working = '') => {
  const id = 'calculation-' + topic + '-' + (index + 1);
  const correct = format(value, digits) + suffix;
  const wrong = [-2, 1, 3].map(offset => format(value + step * offset, digits) + suffix);
  bank.push({ id, category: 'math', mathTopic: topic, topic: MATH_TOPIC_LABELS[topic],
    stem: stem + ' ≈ ?', ...questionChoices(id, correct, wrong),
    explanation: (working ? working + '。' : '') + stem + ' ≈ ' + correct + '。' });
};
const comparison = (topic: Exclude<MathTopic, 'all'>, index: number, expressions: string[], values: number[]) => {
  const id = 'calculation-' + topic + '-' + (index + 1);
  const maximum = Math.max(...values), at = values.indexOf(maximum);
  bank.push({ id, category: 'math', mathTopic: topic, topic: MATH_TOPIC_LABELS[topic], stem: 'max(A, B, C, D) = ?',
    ...questionChoices(id, expressions[at], expressions.filter((_, i) => i !== at)),
    explanation: expressions.map((text, i) => text + ' ≈ ' + format(values[i], 4)).join('；') + '。最大值为 ' + expressions[at] + '。' });
};

const current = [7284, 13972, 26485, 58316, 84725, 196438];
const rates = [12.4, 17.6, -8.5, 23.7, 6.8, -12.3];
const annualStart = [12348, 19574, 51486, 32785, 58367, 84692];
const annualEnd = [16234, 28317, 39754, 51238, 73952, 115796];
const periods = [2, 3, 4, 5, 3, 4];
for (let i = 0; i < 30; i++) {
  const base = i % 6, batch = Math.floor(i / 6);
  const a = current[base] + batch * (2537 + base * 617), rate = Number((rates[base] + batch * .7).toFixed(1)), previous = a / (1 + rate / 100);
  const step = Math.max(5, Math.round(previous * .018));
  numeric('previous', i, a + ' ÷ ' + factor(rate), previous, 0, step);
  numeric('growth', i, a + ' × ' + signedPercent(rate) + ' ÷ ' + factor(rate), a - previous, 0,
    Math.max(5, Math.round(Math.abs(a - previous) * .035)));

  const part = 2384 + i * 1583, total = 8736 + i * 3197;
  numeric('percent', i, part + ' ÷ ' + total, part / total * 100, 2, .75, '%');
  const values = [a, Math.round(a * 1.13 + 117), Math.round(a * .82 + 263), Math.round(a * 1.04 + 359)];
  const growthRates = [12.4 + i, 9.8 + i, 16.6 - i / 2, 11.7 + i / 2].map(n => Number(n.toFixed(1)));
  comparison('growthCompare', i, values.map((n, j) => n + ' × ' + growthRates[j] + '% ÷ ' + factor(growthRates[j])),
    values.map((n, j) => n * growthRates[j] / (100 + growthRates[j])));
  const baseRates = [12.4 + i, 18.7 - i, -4.6 - i / 2, 9.2 + i / 2].map(n => Number(n.toFixed(1)));
  comparison('previousCompare', i, values.map((n, j) => n + ' ÷ ' + factor(baseRates[j])),
    values.map((n, j) => n / (1 + baseRates[j] / 100)));
  const start = annualStart[base] + batch * 3211, end = annualEnd[base] + batch * (3821 + base * 317), yearsCount = periods[base];
  numeric('annualGrowth', i, '(' + end + ' ÷ ' + start + ')^(1 ÷ ' + yearsCount + ') − 1',
    (Math.pow(end / start, 1 / yearsCount) - 1) * 100, 2, .8, '%');

  const numerator = 1347 + i * 846, denominator = 5826 + i * 1073;
  numeric('fractionBelow', i, numerator + ' ÷ ' + denominator, numerator / denominator, 3, .012);
  numeric('fractionAbove', i, denominator + ' ÷ ' + numerator, denominator / numerator, 3, .06);
  const partRate = Number((8.6 + i * 2).toFixed(1)), totalRate = Number((13.2 - i).toFixed(1));
  numeric('previousShare', i, part + ' ÷ ' + total + ' × ' + factor(totalRate) + ' ÷ ' + factor(partRate),
    part / total * (1 + totalRate / 100) / (1 + partRate / 100) * 100, 2, .8, '%');
  const fractions = [[3400 + i * 113, 8200 + i * 173], [4900 + i * 97, 11700 + i * 211],
    [6300 + i * 127, 14800 + i * 191], [7200 + i * 149, 17600 + i * 233]];
  comparison('fractionCompare', i, fractions.map(([n, d]) => n + ' ÷ ' + d), fractions.map(([n, d]) => n / d));
  const years = Array.from({ length: 3 + i % 3 }, (_, j) => current[(i + j) % current.length] + batch * (2117 + j * 293));
  const mean = years.reduce((sum, n) => sum + n, 0) / years.length;
  numeric('annualAverage', i, '(' + years.join(' + ') + ') ÷ ' + years.length, mean, 0,
    Math.max(5, Math.round(mean * .015)));
}

/** No prose scenarios, tables, invented units, or hidden numerical conditions. */
export const CALCULATION_QUESTIONS: Question[] = bank;
